import { action, makeObservable, observable } from "mobx";
import { isRecentEditSessionSnapshotRestorable } from "@/services/editSession";
import type {
  EditSessionState,
  PendingScreenplayUpload,
  PendingSketchImage,
  ProjectDetailsResponse,
  RecentEditSessionSnapshot,
  ScriptWorkflowTransactionId,
  StyleDnaEditSessionThumb,
  StyleDnaPanelDraftPrefillPayload
} from "@/types/project";
import type { ClipTrimRange } from "@/types/timeline";
import type { ScreenplayStartingPoint } from "@/components/ProjectPage/Mainspaces/ScreenplaySourcePicker";
import { DEFAULT_PROJECT_SETTINGS_FORM_VALUES, type ProjectSettingsFormValues } from "@/components/ProjectPage/Mainspaces/projectSettingsShared";

const emptyScriptWorkflowTransactionIds = (): ScriptWorkflowTransactionId => ({
  textExtraction: null,
  scriptGeneration: null,
  shotGeneration: null
});

export interface RunAllCharactersModelOption {
  readonly value: string;
  readonly label: string;
}

/**
 * Live callbacks/state registered by CharactersPanel (which owns the "Run All Characters"
 * business logic - validation, prompt building, per-model defaults) so the Timeline strip
 * header (rendered by the sibling PageContent tree, not a parent/child of CharactersPanel) can
 * render and trigger it without prop-drilling across the whole ProjectPage tree. Cleared on
 * unmount; PageContent only reads this while the Characters tab is active.
 */
export interface RunAllCharactersHandlers {
  readonly disabled: boolean;
  readonly disabledReason: string | null;
  readonly pending: boolean;
  readonly modelOptions: readonly RunAllCharactersModelOption[];
  readonly onRun: (modelId: string) => void;
}

export class ActionStore {
  isHydrated = false;
  projectDetails: ProjectDetailsResponse | null = null;
  scriptWorkflowTransactionIds: ScriptWorkflowTransactionId = emptyScriptWorkflowTransactionIds();

  activeStoryboardShotIndex = 0;

  activeCharacterIndex = 0;

  activeLocationIndex = 0;

  /** Set once the user exits the Script tab's initial "Story & Scenes"/"Visual consistency"/"Skip"
   * chooser via any of its three actions, so it's never shown again for this project - even after a
   * tab switch remounts the Script tab. Reset when switching to a different project. */
  screenplayZeroStateDismissed = false;

  /** The in-progress Script tab upload/settings flow, kept here (rather than component `useState`)
   * so it survives a tab switch away and back before the user clicks "Save changes"/"Save & generate
   * screenplay". `screenplayPendingUpload` holds the file already uploaded to S3 (small + serializable
   * - see `PendingScreenplayUpload`) alongside which track it was uploaded for; `screenplaySetupPageMode`
   * mirrors why Project setup is open (a fresh upload vs. the "Generate breakdown" settings fallback);
   * `screenplaySettingsValues`/`screenplayToneMoodCustomOptions` are the live (unsaved) Project setup
   * form fields - every field in "General", not just the file. All reset on project switch; the upload/
   * track/mode also clear once the upload is saved, replaced, or removed (settings values are left as-is
   * since they get freshly re-derived from the saved project right after). */
  screenplayPendingUpload: PendingScreenplayUpload | null = null;
  screenplayPendingStartingPoint: ScreenplayStartingPoint | null = null;
  screenplaySetupPageMode: "upload" | "breakdown-fallback" | null = null;
  screenplaySettingsValues: ProjectSettingsFormValues = DEFAULT_PROJECT_SETTINGS_FORM_VALUES;
  screenplayToneMoodCustomOptions: readonly string[] = [];
  screenplaySettingsHydrated = false;

  libraryImageDragging = false;
  isDraggingSameAsMainspace = false;
  /** When dragging a library/timeline row that represents a completed video (`visuals[].video_id`). */
  draggingLibraryVideoId: string | null = null;

  styleDnaPanelDraftPrefillVersion = 0;
  styleDnaPanelDraftPrefill: StyleDnaPanelDraftPrefillPayload | null = null;

  styleDnaEditSessionThumb: StyleDnaEditSessionThumb | null = null;

  /** Sketches drawn but not yet saved to the backend, keyed by entity id (character/location/shot). Cleared per-entity on removal or once uploaded at Run time. */
  pendingSketchesByEntityId: Record<string, readonly PendingSketchImage[]> = {};

  /** Inline mainspace "Edit" sessions (Characters/Locations/Image Frame), keyed by entity id. See `EditSessionState`. */
  editSessionsByEntityId: Record<string, EditSessionState> = {};

  /** Snapshots of manually-closed edit sessions, restorable for a short window. See `RecentEditSessionSnapshot`. */
  recentEditSessionsByEntityId: Record<string, RecentEditSessionSnapshot> = {};

  /** See {@link RunAllCharactersHandlers}. */
  runAllCharactersHandlers: RunAllCharactersHandlers | null = null;

  /** Registered by the mounted MainSpace so a Library double-click runs the same code as a drop on it. */
  mainSpaceLibraryDropHandler: ((imageId: string, videoId?: string) => void) | null = null;

  /** Video tab "Cut" trim windows, keyed by `video_id`. Session-only UI state (not persisted to the project). */
  videoTrimRangeByVideoId: Record<string, ClipTrimRange> = {};

  isTimelineSequenceRunning = false;

  /** Session-only undo stack for trim changes (Ctrl+Z). Not observable, not persisted. */
  private trimUndoEntries: Array<{ videoId: string; previousRange: ClipTrimRange | null }> = [];
  private trimUndoLastChangeAt: Record<string, number> = {};
  private isUndoingTrim = false;

  private hydrationPromise: Promise<void> | null = null;

  constructor() {
    makeObservable(this, {
      isHydrated: observable,
      projectDetails: observable,
      scriptWorkflowTransactionIds: observable,
      activeStoryboardShotIndex: observable,
      activeCharacterIndex: observable,
      activeLocationIndex: observable,
      screenplayZeroStateDismissed: observable,
      screenplayPendingUpload: observable,
      screenplayPendingStartingPoint: observable,
      screenplaySetupPageMode: observable,
      screenplaySettingsValues: observable,
      screenplayToneMoodCustomOptions: observable,
      screenplaySettingsHydrated: observable,
      libraryImageDragging: observable,
      isDraggingSameAsMainspace: observable,
      draggingLibraryVideoId: observable,
      styleDnaPanelDraftPrefillVersion: observable,
      styleDnaPanelDraftPrefill: observable,
      styleDnaEditSessionThumb: observable,
      pendingSketchesByEntityId: observable,
      editSessionsByEntityId: observable,
      recentEditSessionsByEntityId: observable,
      runAllCharactersHandlers: observable,
      videoTrimRangeByVideoId: observable,
      isTimelineSequenceRunning: observable,
      setIsHydrated: action,
      setProjectDetails: action,
      clearProjectDetails: action,
      setScriptWorkflowTransactionId: action,
      clearScriptWorkflowTransactionId: action,
      setActiveStoryboardShotIndex: action,
      setActiveCharacterIndex: action,
      setActiveLocationIndex: action,
      setScreenplayZeroStateDismissed: action,
      setScreenplayPendingUpload: action,
      setScreenplayPendingStartingPoint: action,
      setScreenplaySetupPageMode: action,
      setScreenplaySettingsValues: action,
      setScreenplayToneMoodCustomOptions: action,
      setScreenplaySettingsHydrated: action,
      setLibraryImageDragging: action,
      requestStyleDnaPanelDraftPrefill: action,
      clearStyleDnaPanelDraftPrefill: action,
      setStyleDnaEditSessionThumb: action,
      clearStyleDnaEditSessionThumb: action,
      addPendingSketch: action,
      removePendingSketch: action,
      startEditSession: action,
      updateEditSessionDescription: action,
      addEditSessionReferenceImage: action,
      removeEditSessionReferenceImage: action,
      removeEditSessionPinnedReferenceImage: action,
      reorderEditSessionReferenceImages: action,
      endEditSession: action,
      setRunAllCharactersHandlers: action,
      setVideoTrimRange: action,
      clearVideoTrimRange: action,
      undoLastTrimChange: action,
      setTimelineSequenceRunning: action
    });
  }

  /** Records the pre-change value once per drag gesture (calls within 500ms of each other are coalesced). */
  private trackTrimUndo(videoId: string, previousRange: ClipTrimRange | null) {
    if (this.isUndoingTrim) {
      return;
    }

    const now = Date.now();
    const lastChangeAt = this.trimUndoLastChangeAt[videoId] ?? 0;
    this.trimUndoLastChangeAt[videoId] = now;
    if (now - lastChangeAt < 500) {
      return;
    }

    this.trimUndoEntries.push({ videoId, previousRange });
    if (this.trimUndoEntries.length > 50) {
      this.trimUndoEntries.shift();
    }
  }

  /** Ctrl+Z: reverts the most recent trim change (session-only, cleared on project switch). */
  undoLastTrimChange(): boolean {
    const entry = this.trimUndoEntries.pop();
    if (!entry) {
      return false;
    }

    this.isUndoingTrim = true;
    try {
      if (entry.previousRange) {
        this.setVideoTrimRange(entry.videoId, entry.previousRange);
      } else {
        this.clearVideoTrimRange(entry.videoId);
      }
    } finally {
      this.isUndoingTrim = false;
    }

    return true;
  }

  async hydrate(): Promise<void> {
    if (this.hydrationPromise) {
      return this.hydrationPromise;
    }

    this.hydrationPromise = Promise.resolve().then(
      action(() => {
        this.setIsHydrated();
      })
    );

    return this.hydrationPromise;
  }

  setIsHydrated() {
    this.isHydrated = true;
  }

  setProjectDetails(projectDetails: ProjectDetailsResponse | null) {
    const prevProjectId = this.projectDetails?.project_id ?? null;
    const nextProjectId = projectDetails?.project_id ?? null;
    this.projectDetails = projectDetails;

    const clearedProject = nextProjectId === null;
    const switchedBetweenLoadedProjects = prevProjectId !== null && nextProjectId !== null && prevProjectId !== nextProjectId;

    if (clearedProject || switchedBetweenLoadedProjects) {
      this.activeStoryboardShotIndex = 0;
      this.activeCharacterIndex = 0;
      this.activeLocationIndex = 0;
      this.screenplayZeroStateDismissed = false;
      this.screenplayPendingUpload = null;
      this.screenplayPendingStartingPoint = null;
      this.screenplaySetupPageMode = null;
      this.screenplaySettingsValues = DEFAULT_PROJECT_SETTINGS_FORM_VALUES;
      this.screenplayToneMoodCustomOptions = [];
      this.screenplaySettingsHydrated = false;
      this.clearStyleDnaEditSessionThumb();
      this.pendingSketchesByEntityId = {};
      this.editSessionsByEntityId = {};
      this.recentEditSessionsByEntityId = {};
      this.videoTrimRangeByVideoId = {};
    }
  }

  setActiveStoryboardShotIndex(index: number) {
    this.activeStoryboardShotIndex = index;
  }

  setActiveCharacterIndex(index: number) {
    this.activeCharacterIndex = index;
  }

  setActiveLocationIndex(index: number) {
    this.activeLocationIndex = index;
  }

  setRunAllCharactersHandlers(handlers: RunAllCharactersHandlers | null) {
    this.runAllCharactersHandlers = handlers;
  }

  setMainSpaceLibraryDropHandler(handler: ((imageId: string, videoId?: string) => void) | null) {
    this.mainSpaceLibraryDropHandler = handler;
  }

  setScreenplayZeroStateDismissed() {
    this.screenplayZeroStateDismissed = true;
  }

  setScreenplayPendingUpload(upload: PendingScreenplayUpload | null) {
    this.screenplayPendingUpload = upload;
  }

  setScreenplayPendingStartingPoint(startingPoint: ScreenplayStartingPoint | null) {
    this.screenplayPendingStartingPoint = startingPoint;
  }

  setScreenplaySetupPageMode(mode: "upload" | "breakdown-fallback" | null) {
    this.screenplaySetupPageMode = mode;
  }

  setScreenplaySettingsValues(values: ProjectSettingsFormValues) {
    this.screenplaySettingsValues = values;
  }

  setScreenplayToneMoodCustomOptions(options: readonly string[]) {
    this.screenplayToneMoodCustomOptions = options;
  }

  setScreenplaySettingsHydrated() {
    this.screenplaySettingsHydrated = true;
  }

  setLibraryImageDragging(dragging: boolean, sameAsMainspace = false, draggingVideoId: string | null = null) {
    this.libraryImageDragging = dragging;
    this.isDraggingSameAsMainspace = dragging && sameAsMainspace;
    this.draggingLibraryVideoId = dragging ? draggingVideoId : null;
  }

  requestStyleDnaPanelDraftPrefill(payload: StyleDnaPanelDraftPrefillPayload) {
    this.styleDnaPanelDraftPrefill = payload;
    this.styleDnaPanelDraftPrefillVersion += 1;
  }

  clearStyleDnaPanelDraftPrefill() {
    this.styleDnaPanelDraftPrefill = null;
  }

  setStyleDnaEditSessionThumb(value: StyleDnaEditSessionThumb) {
    this.styleDnaEditSessionThumb = value;
  }

  clearStyleDnaEditSessionThumb() {
    this.styleDnaEditSessionThumb = null;
  }

  addPendingSketch(entityId: string, sketch: Omit<PendingSketchImage, "localId">) {
    const current = this.pendingSketchesByEntityId[entityId] ?? [];
    this.pendingSketchesByEntityId = {
      ...this.pendingSketchesByEntityId,
      [entityId]: [...current, { ...sketch, localId: crypto.randomUUID() }]
    };
  }

  removePendingSketch(entityId: string, localId: string) {
    const current = this.pendingSketchesByEntityId[entityId];
    if (!current) {
      return;
    }

    this.pendingSketchesByEntityId = {
      ...this.pendingSketchesByEntityId,
      [entityId]: current.filter((s) => s.localId !== localId)
    };
  }

  /**
   * Enter inline Edit mode for this entity. If it was manually closed for this same entity image within
   * the last `RECENT_EDIT_SESSION_TTL_MS` (see `isRecentEditSessionSnapshotRestorable`), restores that
   * snapshot (references, prompt, and its own sketches) instead of starting blank pinned to
   * `referenceImageId`.
   */
  startEditSession(entityId: string, referenceImageId: string) {
    const snapshot = this.recentEditSessionsByEntityId[entityId];
    const isRestorable = isRecentEditSessionSnapshotRestorable(snapshot, referenceImageId);

    if (snapshot !== undefined) {
      const nextSnapshots = { ...this.recentEditSessionsByEntityId };
      delete nextSnapshots[entityId];
      this.recentEditSessionsByEntityId = nextSnapshots;
    }

    if (isRestorable && snapshot !== undefined) {
      this.editSessionsByEntityId = {
        ...this.editSessionsByEntityId,
        [entityId]: {
          referenceImageId: snapshot.referenceImageId,
          extraReferenceImageIds: snapshot.extraReferenceImageIds,
          descriptionText: snapshot.descriptionText,
          startedAt: Date.now()
        }
      };
      if (snapshot.pendingSketches.length > 0) {
        const current = this.pendingSketchesByEntityId[entityId] ?? [];
        this.pendingSketchesByEntityId = {
          ...this.pendingSketchesByEntityId,
          [entityId]: [...current, ...snapshot.pendingSketches]
        };
      }

      return;
    }

    this.editSessionsByEntityId = {
      ...this.editSessionsByEntityId,
      [entityId]: { referenceImageId, extraReferenceImageIds: [], descriptionText: "", startedAt: Date.now() }
    };
  }

  /** Update the in-progress edit prompt text; no-op if there's no active session for this entity. */
  updateEditSessionDescription(entityId: string, descriptionText: string) {
    const current = this.editSessionsByEntityId[entityId];
    if (!current) {
      return;
    }

    this.editSessionsByEntityId = {
      ...this.editSessionsByEntityId,
      [entityId]: { ...current, descriptionText }
    };
  }

  /** Attach an extra reference image to the active edit session (from its own add button); no-op if there's no active session. */
  addEditSessionReferenceImage(entityId: string, imageId: string) {
    const current = this.editSessionsByEntityId[entityId];
    if (!current || current.extraReferenceImageIds.includes(imageId)) {
      return;
    }

    this.editSessionsByEntityId = {
      ...this.editSessionsByEntityId,
      [entityId]: { ...current, extraReferenceImageIds: [...current.extraReferenceImageIds, imageId] }
    };
  }

  /** Detach an extra reference image from the active edit session; no-op if there's no active session. */
  removeEditSessionReferenceImage(entityId: string, imageId: string) {
    const current = this.editSessionsByEntityId[entityId];
    if (!current) {
      return;
    }

    this.editSessionsByEntityId = {
      ...this.editSessionsByEntityId,
      [entityId]: { ...current, extraReferenceImageIds: current.extraReferenceImageIds.filter((id) => id !== imageId) }
    };
  }

  /** Unpin the session's leading reference image (down to `null`); no-op if already unset or there's no active session. */
  removeEditSessionPinnedReferenceImage(entityId: string) {
    const current = this.editSessionsByEntityId[entityId];
    if (!current || current.referenceImageId === null) {
      return;
    }

    this.editSessionsByEntityId = {
      ...this.editSessionsByEntityId,
      [entityId]: { ...current, referenceImageId: null }
    };
  }

  /** Reorder the active edit session's extra reference images; no-op if there's no active session. */
  reorderEditSessionReferenceImages(entityId: string, nextExtraReferenceImageIds: readonly string[]) {
    const current = this.editSessionsByEntityId[entityId];
    if (!current) {
      return;
    }

    this.editSessionsByEntityId = {
      ...this.editSessionsByEntityId,
      [entityId]: { ...current, extraReferenceImageIds: [...nextExtraReferenceImageIds] }
    };
  }

  /**
   * Exit inline Edit mode for this entity. Pass `saveOptions` only from manual exits (the "Exit edit
   * mode" icon or re-clicking the pen icon) to snapshot the session for `RECENT_EDIT_SESSION_TTL_MS` so
   * it can be restored by `startEditSession`; auto-exits (24h expiry, approve) omit it so nothing is
   * saved.
   */
  endEditSession(entityId: string, saveOptions?: { currentEntityImageId: string | null }) {
    const session = this.editSessionsByEntityId[entityId];
    if (!session) {
      return;
    }

    const next = { ...this.editSessionsByEntityId };
    delete next[entityId];
    this.editSessionsByEntityId = next;

    // Sketches drawn while this session was active are edit-only scratch work - discard any still
    // pending (not yet Run/uploaded) so they don't leak into the normal description box. Sketches that
    // already existed before editing started are untouched, so they still show back in the normal box.
    const pendingSketches = this.pendingSketchesByEntityId[entityId];
    const editModeSketches = pendingSketches?.filter((s) => s.createdInEditMode) ?? [];

    if (saveOptions) {
      this.recentEditSessionsByEntityId = {
        ...this.recentEditSessionsByEntityId,
        [entityId]: {
          referenceImageId: session.referenceImageId,
          extraReferenceImageIds: session.extraReferenceImageIds,
          descriptionText: session.descriptionText,
          pendingSketches: editModeSketches,
          entityImageIdAtClose: saveOptions.currentEntityImageId,
          closedAt: Date.now()
        }
      };
    }

    if (editModeSketches.length > 0) {
      this.pendingSketchesByEntityId = {
        ...this.pendingSketchesByEntityId,
        [entityId]: (pendingSketches ?? []).filter((s) => !s.createdInEditMode)
      };
    }
  }

  setVideoTrimRange(videoId: string, range: ClipTrimRange) {
    this.trackTrimUndo(videoId, this.videoTrimRangeByVideoId[videoId] ?? null);
    this.videoTrimRangeByVideoId = { ...this.videoTrimRangeByVideoId, [videoId]: range };
  }

  clearVideoTrimRange(videoId: string) {
    if (!(videoId in this.videoTrimRangeByVideoId)) {
      return;
    }

    this.trackTrimUndo(videoId, this.videoTrimRangeByVideoId[videoId] ?? null);
    this.videoTrimRangeByVideoId = Object.fromEntries(Object.entries(this.videoTrimRangeByVideoId).filter(([id]) => id !== videoId));
  }

  setTimelineSequenceRunning(running: boolean) {
    this.isTimelineSequenceRunning = running;
  }

  clearProjectDetails() {
    this.projectDetails = null;
    this.activeStoryboardShotIndex = 0;
    this.activeCharacterIndex = 0;
    this.activeLocationIndex = 0;
    this.libraryImageDragging = false;
    this.isDraggingSameAsMainspace = false;
    this.draggingLibraryVideoId = null;
    this.styleDnaPanelDraftPrefill = null;
    this.styleDnaPanelDraftPrefillVersion = 0;
    this.clearStyleDnaEditSessionThumb();
    this.scriptWorkflowTransactionIds = emptyScriptWorkflowTransactionIds();
    this.pendingSketchesByEntityId = {};
    this.editSessionsByEntityId = {};
    this.recentEditSessionsByEntityId = {};
    this.videoTrimRangeByVideoId = {};
    this.trimUndoEntries = [];
    this.trimUndoLastChangeAt = {};
    this.recentEditSessionsByEntityId = {};
  }

  setScriptWorkflowTransactionId(key: keyof ScriptWorkflowTransactionId, transactionId: string | null) {
    this.scriptWorkflowTransactionIds = {
      ...this.scriptWorkflowTransactionIds,
      [key]: transactionId
    };
  }

  clearScriptWorkflowTransactionId(key: keyof ScriptWorkflowTransactionId) {
    this.setScriptWorkflowTransactionId(key, null);
  }

  clearStoredData() {
    this.clearProjectDetails();
  }
}
